import * as THREE from 'three';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';
import JSZip from 'jszip';
import { loadedFilesStore } from './loaders';

/**
 * ModelDownloader class handles exporting loaded 3D models and their assets
 * into a downloadable ZIP archive.
 */
export class ModelDownloader {
  private scene: THREE.Scene | THREE.Group;
  private modelName: string;
  private renderer?: THREE.WebGLRenderer;
  private camera?: THREE.Camera;

  constructor(
    scene: THREE.Scene | THREE.Group,
    modelName: string,
    renderer?: THREE.WebGLRenderer,
    camera?: THREE.Camera,
  ) {
    this.scene = scene;
    this.modelName = modelName;
    this.renderer = renderer;
    this.camera = camera;
  }

  /**
   * Exports the current scene to GLB format
   */
  private async exportToGLB(): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const exporter = new GLTFExporter();

      exporter.parse(
        this.scene,
        (result) => {
          if (result instanceof ArrayBuffer) {
            resolve(result);
          } else {
            // If result is JSON (GLTF), we need to request binary
            reject(new Error('Expected binary GLB output'));
          }
        },
        (error) => {
          reject(error);
        },
        {
          binary: true, // Export as GLB (binary)
          animations: this.collectAnimations(),
          includeCustomExtensions: true,
        },
      );
    });
  }

  /**
   * Collects all animations from the scene
   */
  private collectAnimations(): THREE.AnimationClip[] {
    const animations: THREE.AnimationClip[] = [];
    const seen = new Set<string>();

    // Check animations directly on the scene/group
    if ((this.scene as any).animations && Array.isArray((this.scene as any).animations)) {
      for (const anim of (this.scene as any).animations) {
        if (!seen.has(anim.uuid)) {
          animations.push(anim);
          seen.add(anim.uuid);
        }
      }
    }

    // Traverse the hierarchy
    this.scene.traverse((child) => {
      if (child.animations && child.animations.length > 0) {
        for (const anim of child.animations) {
          if (!seen.has(anim.uuid)) {
            animations.push(anim);
            seen.add(anim.uuid);
          }
        }
      }
    });

    return animations;
  }

  /**
   * Checks if a filename is an image/texture file
   */
  private isTextureFile(filename: string): boolean {
    const textureExtensions = [
      '.jpg',
      '.jpeg',
      '.png',
      '.webp',
      '.bmp',
      '.tga',
      '.gif',
      '.exr',
      '.psd',
      '.tiff',
    ];
    const ext = filename.toLowerCase().substring(filename.lastIndexOf('.'));
    return textureExtensions.includes(ext);
  }

  /**
   * Captures a screenshot of the current 3D view
   */
  private async captureScreenshot(): Promise<Blob | null> {
    if (!this.renderer || !this.camera) {
      console.warn('Renderer or camera not available for screenshot capture');
      return null;
    }

    try {
      // Render the scene to the renderer
      //this.renderer.render(this.scene as THREE.Scene, this.camera);

      // Get the canvas from the renderer
      const canvas = this.renderer.domElement;

      // Convert canvas to blob
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(
          (blob) => {
            resolve(blob);
          },
          'image/png',
          1.0,
        );
      });

      return blob;
    } catch (error) {
      console.error('Failed to capture screenshot:', error);
      return null;
    }
  }

  /**
   * Checks if a filename is a model file (FBX, OBJ, GLTF, etc.)
   */
  private isModelFile(filename: string): boolean {
    const modelExtensions = [
      '.fbx',
      '.obj',
      '.gltf',
      '.glb',
      '.usdz',
      '.usdza',
      '.3ds',
      '.dae',
      '.stl',
    ];
    const ext = filename.toLowerCase().substring(filename.lastIndexOf('.'));
    return modelExtensions.includes(ext);
  }

  /**
   * Creates a ZIP archive containing all loaded files and the exported GLB
   */
  async createArchive(): Promise<Blob> {
    const zip = new JSZip();

    // Get all loaded files from the store
    const loadedFiles = loadedFilesStore.fileUrlCache;

    // Track texture loading status and model files
    const textureStatuses: string[] = [];
    let originalModelAdded = false;

    // Add original files to the archive (with textures in subfolder)
    for (const [filename, url] of loadedFiles) {
      try {
        // Fetch the file data from the URL
        const response = await fetch(url);

        let resultingFilename = filename;
        //if file has no extension, use original node name
        if (!filename.includes('.')) {
          resultingFilename = this.modelName;
        }

        const isTexture = this.isTextureFile(resultingFilename);
        const isModelFile = this.isModelFile(resultingFilename);

        // Check if response is successful
        if (!response.ok) {
          const statusMessage = `${resultingFilename} - Failed (HTTP ${response.status})`;
          if (isTexture) {
            textureStatuses.push(statusMessage);
          }
          console.warn(`Failed to fetch file: ${resultingFilename} (HTTP ${response.status})`);
          continue;
        }

        const data = await response.arrayBuffer();

        // Place texture files in textures subfolder
        const archivePath = isTexture ? `textures/${resultingFilename}` : resultingFilename;

        zip.file(archivePath, data);

        // Track if original model file was added
        if (isModelFile) {
          originalModelAdded = true;
        }

        const statusMessage = `${resultingFilename} - OK`;
        if (isTexture) {
          textureStatuses.push(statusMessage);
        }
        console.log(`Added to archive: ${archivePath}`);
      } catch (error) {
        console.error(`Error processing file ${filename}:`, error);
        const isTexture = this.isTextureFile(filename);
        if (isTexture) {
          textureStatuses.push(
            `${filename} - Error (${error instanceof Error ? error.message : 'Unknown error'})`,
          );
        }
      }
    }

    // If textures failed but original model wasn't added, try to fetch and add it
    if (textureStatuses.length > 0 && !originalModelAdded) {
      const loadedFiles = loadedFilesStore.fileUrlCache;
      for (const [filename, url] of loadedFiles) {
        if (this.isModelFile(filename)) {
          try {
            const response = await fetch(url);
            if (response.ok) {
              const data = await response.arrayBuffer();
              zip.file(filename, data);
              console.log(`Added original model file to archive: ${filename}`);
              originalModelAdded = true;
              break;
            }
          } catch (error) {
            console.error(`Failed to fetch model file ${filename}:`, error);
          }
        }
      }
    }

    // Add textures.txt with texture status information
    if (textureStatuses.length > 0) {
      const texturesContent = textureStatuses.join('\n');
      zip.file('textures.txt', texturesContent);
      console.log('Added textures.txt to archive');
    }

    // Export scene to GLB and add to archive
    try {
      const glbData = await this.exportToGLB();
      const glbFilename = this.getBaseFilename() + '_combined.glb';
      zip.file(glbFilename, glbData);
      console.log(`Added GLB export to archive: ${glbFilename}`);
    } catch (error) {
      console.error('Failed to export GLB:', error);
      // Continue without GLB if export fails
    }

    // Capture and add screenshot to archive
    try {
      const screenshotBlob = await this.captureScreenshot();
      if (screenshotBlob) {
        const previewFilename = this.getBaseFilename() + '_preview.png';
        zip.file(previewFilename, screenshotBlob);
        console.log(`Added preview screenshot to archive: ${previewFilename}`);
      }
    } catch (error) {
      console.error('Failed to capture screenshot:', error);
      // Continue without screenshot if capture fails
    }

    // Generate the ZIP file without compression (STORE method)
    const zipBlob = await zip.generateAsync({
      type: 'blob',
      compression: 'STORE', // No compression
    });

    return zipBlob;
  }

  /**
   * Gets the base filename without extension
   */
  private getBaseFilename(): string {
    const lastDot = this.modelName.lastIndexOf('.');
    return lastDot > 0 ? this.modelName.substring(0, lastDot) : this.modelName;
  }

  /**
   * Downloads the archive as a file
   */
  async download(): Promise<void> {
    const zipBlob = await this.createArchive();
    const zipFilename = this.getBaseFilename() + '_export.zip';

    // Create download link
    const url = URL.createObjectURL(zipBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = zipFilename;

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log(`Downloaded: ${zipFilename}`);
  }
}

/**
 * Helper function to download the current model
 */
export async function downloadModel(
  scene: THREE.Scene | THREE.Group,
  modelName: string,
  renderer?: THREE.WebGLRenderer,
  camera?: THREE.Camera,
): Promise<void> {
  const downloader = new ModelDownloader(scene, modelName, renderer, camera);
  await downloader.download();
}
